/**
 * LanguageSelect Component
 * Dropdown for selecting application language
 * Integrated with i18n for language switching
 */

import * as Select from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { useClientTranslation } from "@/hooks/use-client-translation";
import { cn } from "@/lib/utils";

type SupportedLanguage = "en" | "el";

interface LanguageOption {
  value: SupportedLanguage;
  label: string;
  shortLabel: string;
}

const LANGUAGE_OPTIONS: LanguageOption[] = [
  { value: "en", label: "English", shortLabel: "EN" },
  { value: "el", label: "Ελληνικά", shortLabel: "EL" },
];

const FLAG_IMAGES: Record<SupportedLanguage, string> = {
  en: "/english-flag-circular.svg",
  el: "/greece-flag-circular.svg",
};

function getFlagImage(language: SupportedLanguage): string {
  return FLAG_IMAGES[language];
}

export interface LanguageToggleProps {
  className?: string;
}

export function LanguageToggle({ className }: LanguageToggleProps) {
  const { i18n } = useClientTranslation();

  const language = (i18n.language === "el" ? "el" : "en") as SupportedLanguage;

  const handleValueChange = (value: string) => {
    i18n.changeLanguage(value);
  };

  const currentOption = LANGUAGE_OPTIONS.find(
    (opt) => opt.value === language
  );

  return (
    <Select.Root value={language} onValueChange={handleValueChange}>
      <Select.Trigger
        className={cn(
          "flex items-center justify-between rounded-full border border-black/15 bg-white px-3 py-2 text-sm font-semibold text-black",
          "hover:bg-black/5",
          "focus:outline-none focus:ring-2 focus:ring-purple-primary/20 focus:ring-offset-2",
          className
        )}
        aria-label="Select language"
      >
        <Select.Value placeholder="Select language">
          {currentOption && (
            <span className="flex items-center gap-2">
              <img
                src={getFlagImage(language)}
                alt=""
                className="h-5 w-5 rounded-full"
                aria-hidden="true"
              />
              <span className="font-semibold">
                {currentOption.shortLabel}
              </span>
            </span>
          )}
        </Select.Value>
        <Select.Icon className="ml-2">
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Select.Icon>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          className={cn(
            "relative z-50 min-w-[180px] overflow-hidden rounded-xl border border-black/15 bg-white text-black shadow-md",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
          )}
          position="popper"
          sideOffset={5}
        >
          <Select.Viewport className="p-1">
            {LANGUAGE_OPTIONS.map((option) => (
              <Select.Item
                key={option.value}
                value={option.value}
                className={cn(
                  "relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 px-3 text-sm outline-none",
                  "hover:bg-black/5",
                  "focus:bg-black/5",
                  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                )}
              >
                <Select.ItemText>
                  <span className="flex items-center gap-2">
                    <img
                      src={getFlagImage(option.value)}
                      alt=""
                      className="h-5 w-5 rounded-full"
                      aria-hidden="true"
                    />
                    <span className="font-semibold">
                      {option.label} ({option.shortLabel})
                    </span>
                  </span>
                </Select.ItemText>
                <Select.ItemIndicator className="ml-auto">
                  <Check className="h-4 w-4 text-purple-primary" />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
